import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from "@sentry/react";
import { loadRazorpaySDK, openRazorpayCheckout, getCurrencySymbol, formatCurrency } from '../../services/razorpay';
import { api } from '../../services/api';
import { useAuth } from '../../components/auth/AuthContext';
import './RazorpayCheckout.css';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'payment-checkout' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'payment-checkout' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'payment-checkout',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Payment checkout action: ${action}`,
    category: 'payment-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Payment checkout performance: ${operation} took ${duration}ms`,
    category: 'payment-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const logSecurityEvent = (event, context = {}) => {
  Sentry.captureMessage(`Payment security event: ${event}`, {
    level: 'warning',
    tags: { 
      section: 'payment-security',
      event_type: 'security_event'
    },
    extra: context
  });
};

const ERROR_MESSAGES = {
  // Existing messages
  'SDK_LOAD_FAILED': 'Payment system unavailable. Please try again later.',
  'ORDER_CREATION_FAILED': 'Unable to create payment order. Please try again.',
  'PAYMENT_PROCESSING_FAILED': 'Payment processing failed. Please try again.',
  'PAYMENT_VERIFICATION_FAILED': 'Payment verification failed. Please contact support.',
  'PROFILE_INCOMPLETE': 'Please complete your profile before making payment',
  'INVALID_AMOUNT': 'Invalid payment amount',
  
  'NETWORK_ERROR': 'Network error. Please check your connection.',
  'SERVER_ERROR': 'Service temporarily unavailable',
  'RATE_LIMIT_EXCEEDED': 'Too many payment attempts. Please wait 60 seconds before trying again',
  'PAYMENT_CANCELLED': 'Payment was cancelled',
  'TIMEOUT_ERROR': 'Payment processing timed out. Please try again.',
  
  // New mappings for backend error codes
  'MISSING_USER_PROFILE': 'User profile information missing. Please complete your profile.',
  'INVALID_REQUEST': 'Invalid request format. Please try again.',
  'MISSING_PARAMETERS': 'Required payment information is missing. Please try again.',
  'SERVICE_CONFIG_ERROR': 'Payment service configuration error. Please try again later.',
  'VERIFICATION_FAILED': 'Payment verification failed. Please contact support if payment was deducted.',
  'PAYMENT_DETAILS_ERROR': 'Error retrieving payment details. Please try again or contact support.',
  'PAYMENT_NOT_CAPTURED': 'Payment was not completed successfully. Please try again.',
  'UNAUTHORIZED': 'You are not authorized to access this payment. Please try again.',
  'DB_ERROR': 'Database error while processing payment. Please contact support.',
  'INTERNAL_ERROR': 'An internal error occurred. Please try again or contact support.',
  'MISSING_AMOUNT': 'Payment amount is required. Please specify an amount.',
  'INCOMPLETE_PROFILE': 'Please complete your profile before making a payment.',
  'BELOW_MINIMUM_AMOUNT': 'Payment amount is below the minimum required. Please increase the amount.',
  'CURRENCY_ERROR': 'Currency conversion error. Please try again.',
  'PAYMENT_API_ERROR': 'Failed to create payment order. Please try again later.',
  'DUPLICATE_ORDER': 'This payment order already exists. Please refresh and try again.',
  
  // Payment-specific additions
  'INSUFFICIENT_CREDITS': 'Insufficient credits. Please add funds to your account.',
  'PAYMENT_GATEWAY_ERROR': 'Payment gateway error. Please try again or use a different payment method.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  // Add to ERROR_MESSAGES object in both files
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Amount validation function
const validateAmount = (amount) => {
  if (!amount) return { valid: false, error: 'Amount is required' };
  
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return { valid: false, error: 'Invalid amount format' };
  if (numericAmount <= 0) return { valid: false, error: 'Amount must be greater than zero' };
  if (numericAmount > 1000000) return { valid: false, error: 'Amount exceeds maximum limit' };
  
  return { valid: true };
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  // Check error code (interceptor normalizes to err.code)
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.status === 401) {
    return 'Please log in again to continue';
  }
  
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  
  return ERROR_MESSAGES['SERVER_ERROR'];
};
// Security: Validate error object structure
const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};

const RazorpayCheckout = ({ 
  amount, 
  currency = 'USD', 
  onSuccess, 
  onError, 
  onCancel,
  orderId = null,
  keyId = null,
  userInfo = null
}) => {
  const { handleAuthError } = useAuth();
  
  // Performance: Controlled loading states
  const [loadingStates, setLoadingStates] = useState({
    sdk: true,
    order: false,
    payment: false
  });
  
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [userCurrency, setUserCurrency] = useState(currency);
  const [paymentLocked, setPaymentLocked] = useState(false);

  // Performance: Request cancellation
  const abortControllerRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  const paymentInProgressRef = useRef(false);
  const paymentAttemptsRef = useRef(0);
  const MAX_PAYMENT_ATTEMPTS = 3;

  // Security: Rate limiting
  const lastPaymentAttemptRef = useRef(0);
  const PAYMENT_COOLDOWN = 3000; // 3 seconds

  // Component lifecycle logging
  useEffect(() => {
    logInfo('RazorpayCheckout component mounted', {
      amount,
      currency,
      hasOrderId: !!orderId,
      hasKeyId: !!keyId,
      hasUserInfo: !!userInfo
    });
    
    logUserAction('checkout_component_mounted', {
      amount,
      currency,
      providedOrderId: !!orderId
    });

    // Set checkout context for Sentry
    Sentry.setContext("payment-checkout", {
      amount,
      currency,
      hasOrderId: !!orderId,
      hasUserInfo: !!userInfo,
      mountedAt: new Date().toISOString()
    });
  }, [amount, currency, orderId, keyId, userInfo]);

  // Reset payment attempts after 5 minutes
  useEffect(() => {
    const resetTimer = setInterval(() => {
      paymentAttemptsRef.current = 0;
      logInfo('Payment attempts counter reset');
    }, 300000); // 5 minutes
    
    return () => clearInterval(resetTimer);
  }, []);

  // Performance: Memoized amount validation
  const amountValidation = useMemo(() => {
    const validation = validateAmount(amount);
    
    if (!validation.valid) {
      logError('Amount validation failed', {
        amount,
        validationError: validation.error
      });
      
      logSecurityEvent('invalid_amount_detected', {
        amount,
        error: validation.error
      });
    } else {
      logInfo('Amount validation passed', {
        amount,
        numericAmount: typeof amount === 'string' ? parseFloat(amount) : amount
      });
    }
    
    return validation;
  }, [amount]);

  // Performance: Memoized sanitized user info
  const sanitizedUserInfo = useMemo(() => {
    if (!userInfo) {
      logInfo('No user info provided for checkout');
      return null;
    }
    
    const sanitized = {
      name: sanitizeText(userInfo.name || ''),
      email: sanitizeText(userInfo.email || ''),
      contact: sanitizeText(userInfo.contact || '')
    };
    
    logInfo('User info sanitized for checkout', {
      hasName: !!sanitized.name,
      hasEmail: !!sanitized.email,
      hasContact: !!sanitized.contact
    });
    
    return sanitized;
  }, [userInfo]);

  // Security: Rate limiting check
  const checkPaymentRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastAttempt = now - lastPaymentAttemptRef.current;
    
    if (timeSinceLastAttempt < PAYMENT_COOLDOWN) {
      logSecurityEvent('payment_rate_limited', {
        timeSinceLastAttempt,
        cooldownPeriod: PAYMENT_COOLDOWN
      });
      
      logUserAction('payment_rate_limited', {
        timeSinceLastAttempt,
        cooldownPeriod: PAYMENT_COOLDOWN
      });
      
      return false;
    }
    
    lastPaymentAttemptRef.current = now;
    return true;
  }, []);

  // Get user's preferred currency
  useEffect(() => {
    if (orderId) return; // Skip if order already provided
    
    const fetchUserCurrency = async () => {
      const startTime = performance.now();
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      logInfo('Fetching user preferred currency');
      
      try {
        const response = await api.getUserProfile();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('fetch_user_currency', duration, {
          success: !!response.success
        });
        
        if (validateErrorObject(response)) {
          if (response.success && response.account_currency) {
            const preferredCurrency = sanitizeText(response.account_currency);
            setUserCurrency(preferredCurrency);
            
            logInfo('User preferred currency retrieved', {
              currency: preferredCurrency,
              duration: `${duration.toFixed(2)}ms`
            });
          }
        }
      } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (err.name === 'AbortError') {
          logInfo('User currency fetch aborted');
          return;
        }
        
        if (err.response?.status === 401 || err.status === 401) {
          handleAuthError();
          return;
        }
        
        logError('Failed to fetch user currency', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name,
          fallbackCurrency: currency
        }, err);
        
        // Silent fallback to provided currency
      }
    };
    
    fetchUserCurrency();
  }, [currency, orderId, handleAuthError]);
  
  // Load Razorpay SDK on component mount
  useEffect(() => {
    const loadSdk = async () => {
      const startTime = performance.now();
      
      setLoadingStates(prev => ({ ...prev, sdk: true }));
      setError(null);
      
      logInfo('Loading Razorpay SDK');
      logUserAction('sdk_load_initiated');
      
      try {
        const loaded = await loadRazorpaySDK();
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        setSdkLoaded(loaded);
        
        logPerformance('razorpay_sdk_load', duration, {
          success: loaded
        });
        
        if (loaded) {
          logInfo('Razorpay SDK loaded successfully', {
            duration: `${duration.toFixed(2)}ms`
          });
          
          logUserAction('sdk_load_success', {
            duration: `${duration.toFixed(2)}ms`
          });
        } else {
          const safeMessage = getSafeErrorMessage({ code: 'SDK_LOAD_FAILED' });
          setError(safeMessage);
          
          logError('Razorpay SDK failed to load', {
            duration: `${duration.toFixed(2)}ms`
          });
          
          logUserAction('sdk_load_failed', {
            duration: `${duration.toFixed(2)}ms`
          });
        }
      } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        const safeMessage = getSafeErrorMessage({ code: 'SDK_LOAD_FAILED' });
        setError(safeMessage);
        
        logError('Razorpay SDK load error', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name,
          errorMessage: err.message
        }, err);
        
        logUserAction('sdk_load_error', {
          error: err.message,
          duration: `${duration.toFixed(2)}ms`
        });
      } finally {
        setLoadingStates(prev => ({ ...prev, sdk: false }));
      }
    };

    loadSdk();
  }, []);
// Create order or use provided order data
useEffect(() => {
  // If order data provided through props, use it
  if (orderId && keyId) {
    if (!amountValidation.valid) {
      const safeMessage = getSafeErrorMessage({ code: 'INVALID_AMOUNT' });
      setError(safeMessage);
      
      logError('Order creation skipped - invalid amount', {
        amount,
        validationError: amountValidation.error
      });
      
      return;
    }
    
    const orderInfo = {
      order_id: sanitizeText(orderId),
      key_id: sanitizeText(keyId),
      currency: sanitizeText(currency),
      total_amount: amount,
      name: sanitizedUserInfo?.name || '',
      email: sanitizedUserInfo?.email || '',
      contact: sanitizedUserInfo?.contact || ''
    };
    
    setOrderData(orderInfo);
    
    logInfo('Using provided order data', {
      orderId: sanitizeText(orderId),
      currency: sanitizeText(currency),
      amount,
      hasUserInfo: !!sanitizedUserInfo
    });
    
    logUserAction('order_data_provided', {
      orderId: sanitizeText(orderId),
      amount,
      currency: sanitizeText(currency)
    });
    
    return;
  }
  
  // Create new order if none provided
  if (!orderId && sdkLoaded && amountValidation.valid) {
    const createOrder = async () => {
      const startTime = performance.now();
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      setLoadingStates(prev => ({ ...prev, order: true }));
      setError(null);
      
      logInfo('Creating payment order', {
        amount,
        currency: userCurrency
      });
      
      logUserAction('order_creation_initiated', {
        amount,
        currency: userCurrency
      });
      
      // Set timeout for order creation
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const safeMessage = getSafeErrorMessage({ code: 'TIMEOUT_ERROR' });
        setError(safeMessage);
        setLoadingStates(prev => ({ ...prev, order: false }));
        
        logError('Order creation timeout', {
          amount,
          currency: userCurrency,
          timeout: 15000
        });
        
        logUserAction('order_creation_timeout', {
          amount,
          timeout: 15000
        });
      }, 15000); // 15 second timeout
      
      try {
        const response = await api.createPaymentWithProfile(amount, userCurrency);
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        clearTimeout(timeoutId);
        
        logPerformance('order_creation', duration, {
          success: !!response.success,
          amount,
          currency: userCurrency
        });
        
        if (validateErrorObject(response)) {
          if (response.success && response.order_id) {
            const orderInfo = {
              ...response,
              order_id: sanitizeText(response.order_id),
              key_id: sanitizeText(response.key_id),
              currency: sanitizeText(response.currency || userCurrency),
              name: sanitizeText(response.name || ''),
              email: sanitizeText(response.email || ''),
              contact: sanitizeText(response.contact || '')
            };
            
            setOrderData(orderInfo);
            
            logInfo('Order created successfully', {
              orderId: sanitizeText(response.order_id),
              amount,
              currency: sanitizeText(response.currency || userCurrency),
              duration: `${duration.toFixed(2)}ms`
            });
            
            logUserAction('order_creation_success', {
              orderId: sanitizeText(response.order_id),
              amount,
              duration: `${duration.toFixed(2)}ms`
            });

            // Set order creation context
            Sentry.setContext("order-creation", {
              orderId: sanitizeText(response.order_id),
              amount,
              currency: sanitizeText(response.currency || userCurrency),
              createdAt: new Date().toISOString()
            });
            
          } else if (response.error === 'incomplete_profile') {
            const safeMessage = getSafeErrorMessage({ code: 'PROFILE_INCOMPLETE' });
            setError(safeMessage);
            
            logError('Order creation failed - incomplete profile', {
              duration: `${duration.toFixed(2)}ms`,
              redirectTo: response.redirect_to
            });
            
            logUserAction('order_creation_failed_incomplete_profile', {
              duration: `${duration.toFixed(2)}ms`
            });
            
            // Safe redirect
            const redirectUrl = response.redirect_to && typeof response.redirect_to === 'string' 
              ? sanitizeText(response.redirect_to) 
              : '/account';
            
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 2000);
          } else {
            const safeMessage = getSafeErrorMessage(response);
            setError(safeMessage);
            
            logError('Order creation failed', {
              duration: `${duration.toFixed(2)}ms`,
              errorCode: response.code,
              errorMessage: response.message
            });
            
            logUserAction('order_creation_failed', {
              errorCode: response.code,
              duration: `${duration.toFixed(2)}ms`
            });
          }
        } else {
          const safeMessage = getSafeErrorMessage({ code: 'ORDER_CREATION_FAILED' });
          setError(safeMessage);
          
          logError('Order creation failed - invalid response', {
            duration: `${duration.toFixed(2)}ms`,
            responseType: typeof response
          });
          
          logUserAction('order_creation_failed_invalid_response', {
            duration: `${duration.toFixed(2)}ms`
          });
        }
      } catch (err) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        clearTimeout(timeoutId);
        
        // AbortError is not a real error
        if (err.name === 'AbortError') {
          logInfo('Order creation aborted', {
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        // 1. Check 401 FIRST
        if (err.response?.status === 401 || err.status === 401) {
          handleAuthError();
          return;
        }
        
        // 2. Check backend error codes
        if (err.response?.data?.code && ERROR_MESSAGES[err.response.data.code]) {
          const safeMessage = ERROR_MESSAGES[err.response.data.code];
          setError(safeMessage);
          
          logError('Order creation error with backend code', {
            code: err.response.data.code,
            duration: `${duration.toFixed(2)}ms`
          }, err);
          
          logUserAction('order_creation_failed', {
            errorCode: err.response.data.code,
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        // 3. Check direct error code
        if (err.code && ERROR_MESSAGES[err.code]) {
          const safeMessage = ERROR_MESSAGES[err.code];
          setError(safeMessage);
          
          logError('Order creation error with code', {
            code: err.code,
            duration: `${duration.toFixed(2)}ms`
          }, err);
          
          logUserAction('order_creation_failed', {
            errorCode: err.code,
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        // 4. Check HTTP status codes
        if (err.response?.status === 429 || err.status === 429) {
          const retryAfter = err.response?.headers['retry-after'] || 60;
          const safeMessage = `Too many requests. Please wait ${retryAfter} seconds.`;
          setError(safeMessage);
          
          logError('Order creation rate limited', {
            retryAfter,
            duration: `${duration.toFixed(2)}ms`
          }, err);
          
          logUserAction('order_creation_rate_limited', {
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        if (err.response?.status >= 500 || err.status >= 500) {
          const safeMessage = 'Service temporarily unavailable. Please try again.';
          setError(safeMessage);
          
          logError('Order creation service error', {
            status: err.response?.status || err.status,
            duration: `${duration.toFixed(2)}ms`
          }, err);
          
          logUserAction('order_creation_service_error', {
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        // 5. Check network errors - FIXED: Use err.error first
        if (!err.response) {
          const safeMessage = err.error || getSafeErrorMessage({ code: 'NETWORK_ERROR' });
          setError(safeMessage);
          
          logError('Order creation network error', {
            duration: `${duration.toFixed(2)}ms`,
            interceptorMessage: err.error,
            interceptorCode: err.code
          }, err);
          
          logUserAction('order_creation_network_error', {
            error: err.message,
            duration: `${duration.toFixed(2)}ms`
          });
          return;
        }
        
        // 6. Fallback
        const safeMessage = getSafeErrorMessage({ code: 'ORDER_CREATION_FAILED' });
        setError(safeMessage);
        
        logError('Order creation failed', {
          duration: `${duration.toFixed(2)}ms`,
          errorName: err.name,
          errorMessage: err.message
        }, err);
        
        logUserAction('order_creation_failed', {
          error: err.message,
          duration: `${duration.toFixed(2)}ms`
        });
      } finally {
        setLoadingStates(prev => ({ ...prev, order: false }));
      }
    };
    
    createOrder();
  }
}, [amount, sdkLoaded, userCurrency, orderId, keyId, sanitizedUserInfo, currency, amountValidation, handleAuthError]);
  // Performance: Enhanced payment handler with proper error handling


// Performance: Enhanced payment handler with proper error handling
const handlePayment = useCallback(async () => {
  if (!orderData || !orderData.order_id || loadingStates.payment) return;
  
  // Prevent double payment submission
  if (paymentInProgressRef.current) {
    logInfo('Payment already in progress, ignoring duplicate request');
    return;
  }
  
  paymentInProgressRef.current = true;
  
  const startTime = performance.now();
  
  // Security: Rate limiting check
  if (!checkPaymentRateLimit()) {
    paymentInProgressRef.current = false;
    const safeMessage = getSafeErrorMessage({ code: 'RATE_LIMIT_EXCEEDED' });
    setError(safeMessage);
    return;
  }
  
  // Check attempt limit
  if (paymentAttemptsRef.current >= MAX_PAYMENT_ATTEMPTS) {
    paymentInProgressRef.current = false;
    setError('Maximum payment attempts reached. Please wait 5 minutes or refresh the page.');
    logError('Payment attempt limit reached', {
      attempts: paymentAttemptsRef.current,
      maxAttempts: MAX_PAYMENT_ATTEMPTS
    });
    return;
  }
  
  paymentAttemptsRef.current++;
  logInfo('Payment attempt', { 
    attemptNumber: paymentAttemptsRef.current,
    maxAttempts: MAX_PAYMENT_ATTEMPTS 
  });
  
  // Security: Final amount validation
  if (!amountValidation.valid) {
    paymentInProgressRef.current = false;
    const safeMessage = getSafeErrorMessage({ code: 'INVALID_AMOUNT' });
    setError(safeMessage);
    
    logError('Payment blocked - invalid amount', {
      amount,
      validationError: amountValidation.error
    });
    
    return;
  }
  
  setLoadingStates(prev => ({ ...prev, payment: true }));
  setError(null);
  
  logInfo('Payment processing initiated', {
    orderId: orderData.order_id,
    amount: orderData.total_amount,
    currency: orderData.currency || userCurrency
  });
  
  logUserAction('payment_initiated', {
    orderId: orderData.order_id,
    amount: orderData.total_amount,
    currency: orderData.currency || userCurrency
  });
  
  // Clear any existing timeout
  if (paymentTimeoutRef.current) {
    clearTimeout(paymentTimeoutRef.current);
  }
  
  // Set payment processing timeout
  paymentTimeoutRef.current = setTimeout(() => {
    const safeMessage = getSafeErrorMessage({ code: 'TIMEOUT_ERROR' });
    setError(safeMessage);
    setLoadingStates(prev => ({ ...prev, payment: false }));
    
    logError('Payment processing timeout', {
      orderId: orderData.order_id,
      timeout: 300000
    });
    
    logUserAction('payment_timeout', {
      orderId: orderData.order_id,
      timeout: 300000
    });
  }, 300000); // 5 minute timeout for payment flow
  
  try {
    const options = {
      key: orderData.key_id,
      amount: parseFloat(orderData.total_amount) * 100,
      currency: orderData.currency || userCurrency || currency,
      name: "Barrack",
      description: "GPU Credits Purchase",
      order_id: orderData.order_id,
      prefill: {
        name: sanitizedUserInfo?.name || orderData.name || '',
        email: sanitizedUserInfo?.email || orderData.email || '',
        contact: sanitizedUserInfo?.contact || orderData.contact || ''
      },
   notes: {
  barrack_id: sanitizeText(orderData.barrack_id || ''),
  purpose: 'credit_purchase'
},
      tax: {
        rate: 0,
        applicable: false
      },
      readonly: {
        tax: true 
      },
      theme: {
        color: "#3399cc"
      }
    };
    
    logInfo('Opening Razorpay checkout', {
      orderId: orderData.order_id,
      keyId: orderData.key_id,
      amount: options.amount,
      currency: options.currency
    });
    
    const paymentResponse = await openRazorpayCheckout(options);
    
    // Lock payment button for 5 seconds
    setPaymentLocked(true);
    setTimeout(() => {
      setPaymentLocked(false);
    }, 5000);
    
    const checkoutEndTime = performance.now();
    const checkoutDuration = checkoutEndTime - startTime;
    
    logPerformance('payment_checkout', checkoutDuration, {
      orderId: orderData.order_id,
      success: !!paymentResponse
    });
  
    // Security: Validate payment response
    if (!paymentResponse || !paymentResponse.razorpay_payment_id) {
      throw new Error('Invalid payment response');
    }
    
    logInfo('Payment response received, verifying', {
      orderId: orderData.order_id,
      hasPaymentId: !!paymentResponse.razorpay_payment_id,
      hasSignature: !!paymentResponse.razorpay_signature
    });
    
    logUserAction('payment_response_received', {
      orderId: orderData.order_id,
      hasPaymentId: !!paymentResponse.razorpay_payment_id
    });
    
    const verificationStartTime = performance.now();
    const verificationResponse = await api.verifyRazorpayPayment({
      razorpay_payment_id: sanitizeText(paymentResponse.razorpay_payment_id),
      razorpay_order_id: sanitizeText(paymentResponse.razorpay_order_id),
      razorpay_signature: sanitizeText(paymentResponse.razorpay_signature)
    });
    const verificationEndTime = performance.now();
    const verificationDuration = verificationEndTime - verificationStartTime;
    
    logPerformance('payment_verification', verificationDuration, {
      orderId: orderData.order_id,
      success: !!verificationResponse.success
    });
    
    if (validateErrorObject(verificationResponse)) {
      if (verificationResponse.success) {
        const totalDuration = verificationEndTime - startTime;
        
        // Reset attempt counter on success
        paymentAttemptsRef.current = 0;
        
        logPerformance('payment_complete', totalDuration, {
          orderId: orderData.order_id,
          success: true
        });
        
        logInfo('Payment completed successfully', {
          orderId: orderData.order_id,
          verificationDuration: `${verificationDuration.toFixed(2)}ms`,
          totalDuration: `${totalDuration.toFixed(2)}ms`
        });
        
        logUserAction('payment_success', {
          orderId: orderData.order_id,
          totalDuration: `${totalDuration.toFixed(2)}ms`
        });

        // Set payment success context
        Sentry.setContext("payment-success", {
          orderId: orderData.order_id,
          amount: orderData.total_amount,
          currency: orderData.currency || userCurrency,
          completedAt: new Date().toISOString()
        });
        
        if (typeof onSuccess === 'function') {
          onSuccess(verificationResponse);
        }
      } else {
        const safeMessage = getSafeErrorMessage(verificationResponse);
        throw new Error(safeMessage);
      }
    } else {
      const safeMessage = getSafeErrorMessage({ code: 'PAYMENT_VERIFICATION_FAILED' });
      throw new Error(safeMessage);
    }
 } catch (err) {
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  let safeMessage;
  
  // Payment cancellation check (user action, not an error)
  if (err.error && err.error.code === 'payment_cancelled') {
    safeMessage = getSafeErrorMessage({ code: 'PAYMENT_CANCELLED' });
    
    logUserAction('payment_cancelled', {
      orderId: orderData.order_id,
      duration: `${duration.toFixed(2)}ms`
    });
    
    logInfo('Payment cancelled by user', {
      orderId: orderData.order_id,
      duration: `${duration.toFixed(2)}ms`
    });
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 1: Authentication error
  if (err.status === 401) {
    handleAuthError();
    return;
  }
  
  // Position 2: Network error
  const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
  if (networkErrorCodes.includes(err.code)) {
    safeMessage = err.error;
    
    logError('Payment network error', {
      orderId: orderData.order_id,
      code: err.code,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 3: Rate limit
  if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
    safeMessage = err.error;
    
    logError('Payment rate limited', {
      orderId: orderData.order_id,
      status: err.status,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 4: Insufficient credits (402)
  if (err.status === 402) {
    safeMessage = 'Insufficient credits. Please add funds to your account.';
    
    logError('Insufficient credits for payment', {
      orderId: orderData.order_id,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 5: Server errors (500+)
  if (err.status >= 500) {
    safeMessage = 'Payment service temporarily unavailable. Please try again.';
    
    logError('Payment service error', {
      orderId: orderData.order_id,
      status: err.status,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 6: Mapped error code
  if (err.code && ERROR_MESSAGES[err.code]) {
    safeMessage = ERROR_MESSAGES[err.code];
    
    logError('Payment processing error - mapped code', {
      orderId: orderData.order_id,
      code: err.code,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    setError(safeMessage);
    if (typeof onError === 'function') {
      onError(new Error(safeMessage));
    }
    return;
  }
  
  // Position 7: Fallback
  safeMessage = err.error || getSafeErrorMessage({ code: 'PAYMENT_PROCESSING_FAILED' });
  
  logError('Payment processing failed', {
    orderId: orderData.order_id,
    duration: `${duration.toFixed(2)}ms`,
    errorName: err.name
  }, err);
  
  logPerformance('payment_failed', duration, {
    orderId: orderData.order_id,
    errorType: err.code || err.name
  });
  
  logUserAction('payment_failed', {
    orderId: orderData.order_id,
    errorType: err.code || err.name,
    duration: `${duration.toFixed(2)}ms`
  });

  Sentry.setContext("payment-failure", {
    orderId: orderData.order_id,
    amount: orderData.total_amount,
    currency: orderData.currency || userCurrency,
    errorType: err.code || err.name,
    failedAt: new Date().toISOString()
  });
  
  setError(safeMessage);
  
  if (typeof onError === 'function') {
    onError(new Error(safeMessage));
  }
} finally {
  paymentInProgressRef.current = false;
  if (paymentTimeoutRef.current) {
    clearTimeout(paymentTimeoutRef.current);
  }
  setLoadingStates(prev => ({ ...prev, payment: false }));
}
}, [orderData, loadingStates.payment, checkPaymentRateLimit, amountValidation, sanitizedUserInfo, userCurrency, currency, onSuccess, onError, amount, handleAuthError]);


  // Handle cancellation
  const handleCancel = useCallback(() => {
    logUserAction('payment_cancelled_by_user', {
      orderId: orderData?.order_id,
      hadError: !!error
    });
    
    logInfo('Payment cancelled by user', {
      orderId: orderData?.order_id,
      hadError: !!error,
      cancelledAt: new Date().toISOString()
    });
    
    if (typeof onCancel === 'function') {
      onCancel();
    }
  }, [onCancel, orderData?.order_id, error]);

  // Performance: Request cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
      }
      
      logInfo('RazorpayCheckout component unmounted', {
        orderId: orderData?.order_id,
        hadError: !!error
      });
    };
  }, [orderData?.order_id, error]);
  
  if (loadingStates.sdk || loadingStates.order) {
    return (
      <div className="razorpay-checkout-container">
        {orderData && (
          <div className="order-summary">
            <p>Amount: {formatCurrency(orderData.total_amount, sanitizeText(orderData.currency || userCurrency))}</p>
          </div>
        )}
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>{loadingStates.sdk ? 'Initializing payment...' : 'Creating order...'}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="razorpay-checkout-container">
        <div className="error-container">
          <p className="error-message">{sanitizeText(error)}</p>
          <button className="cancel-button" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!amountValidation.valid) {
    return (
      <div className="razorpay-checkout-container">
        <div className="error-container">
          <p className="error-message">{sanitizeText(amountValidation.error)}</p>
          <button className="cancel-button" onClick={handleCancel}>
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="razorpay-checkout-container">
      {orderData && (
        <div className="checkout-actions">
          <button
            className="checkout-button"
            onClick={handlePayment}
            disabled={loadingStates.payment || paymentLocked}
          >
            {loadingStates.payment ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      )}
    </div>
  );
};

RazorpayCheckout.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currency: PropTypes.string,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onCancel: PropTypes.func,
  orderId: PropTypes.string,
  keyId: PropTypes.string,
  userInfo: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    contact: PropTypes.string
  })
};

export default RazorpayCheckout;